import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { from, switchMap, catchError, throwError } from 'rxjs';
import { Router } from '@angular/router';

import { KeycloakAuthService } from '../auth/keycloak.service';
import { SharedFacade } from '../../facades/shared.facade';
import { UserAuthService } from '../auth/user-auth.service';
import { Injector, inject } from '@angular/core';
import { environment } from '../../../../environments/environment';

/**
 * Verrou de module : empêche un déclenchement concurrent de logout
 * lorsque plusieurs requêtes en vol reçoivent 401 simultanément.
 * Réinitialisé après l'appel logout() (qui provoque normalement une redirection
 * et un déchargement du document — le flag ne survit donc pas en pratique).
 */
let autoLogoutInFlight = false;

export const keycloakInterceptor: HttpInterceptorFn = (req, next) => {
  const keycloakService = inject(KeycloakAuthService);
  const sharedFacade = inject(SharedFacade);
  // Résolution paresseuse de UserAuthService via l'Injector :
  // une injection synchrone créerait un cycle DI au démarrage
  // (UserAuthService → SessionCleanup → FormioOffline → ... → HttpClient
  //  alors que HttpClient est en cours de construction).
  // L'Injector lui-même est sûr à injecter directement.
  const injector = inject(Injector);
  const router = inject(Router);

  // En SSR, on passe la requête sans modification
  if (typeof window === 'undefined') {
    return next(req);
  }

  // Liste des URLs à exclure de l'authentification
  const excludedUrls = [
    '/assets/',
    '/api/public/',
    environment.keycloak.url,
    '/status-check/',
    // Étape 2 du flux de téléchargement : `/file/dl/<token>` porte son propre
    // jeton signé dans l'URL et n'attend aucun `Authorization`
    // (cf. `documents.facade.ts`, « le token est auto-porteur, pas de JWT
    // requis »). Y attacher un Bearer de consultation revenait à diffuser ce
    // jeton à une requête qui n'en a pas l'usage.
    '/file/dl/',
    // Ouverture de session du portail hérité (`x-token`) : elle s'authentifie
    // par son propre corps, et sert un usager **sans compte**. Y attacher un
    // Bearer serait au mieux inutile ; au pire, un 401 sur cette route
    // déclencherait la déconnexion automatique d'un usager par ailleurs
    // légitimement connecté.
    '/portals/auth/login'
  ];

  const shouldExclude = excludedUrls.some(url => req.url.includes(url));
  if (shouldExclude) {
    return next(req);
  }

  // ── L'appelant a déjà posé son `Authorization` : on n'y touche pas. ────────
  //
  // `req.clone({ setHeaders })` applique `headers.set()`, qui **écrase** un
  // en-tête existant. Or plusieurs appels transmettent délibérément le jeton
  // qu'ils tiennent en mémoire, seul jeton dont ils savent qu'il correspond au
  // dossier affiché :
  //   - `requests.api.ts` → `GET /records/anonymous/consult` (le back-end
  //     résout le dossier depuis le jeton lui-même, rien dans l'URL) ;
  //   - `services.api.ts` → `POST /forms/public-submit` (jeton d'écriture rendu
  //     par `submit-record`, propre au dépôt en cours).
  // L'intercepteur détruisait ce jeton correct pour lui substituer celui du
  // stockage — c'est-à-dire, en multi-onglets, celui du **dernier** dossier
  // ouvert. D'où le symptôme : soumettre dans deux onglets puis payer depuis
  // l'un envoyait le jeton de l'autre.
  //
  // Pass-through complet (et non simple non-écrasement) : ces appels portent un
  // jeton `status-check` de courte durée, dont l'expiration est gérée par les
  // composants appelants (`consult-page.component.ts` sur 401 →
  // `onTokenExpired()`). Déclencher en plus l'auto-logout Keycloak du bloc
  // ci-dessous n'aurait aucun sens pour un invité, qui n'a pas de session SSO.
  if (req.headers.has('Authorization')) {
    return next(req);
  }

  // Logique principale avec switchMap pour éviter les multiples appels
  return from(getToken(req, keycloakService, sharedFacade, router)).pipe(
    switchMap(token => {
      const hadToken = !!token;
      const outgoingReq = hadToken
        ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
        : req;

      return next(outgoingReq).pipe(
        catchError((err: unknown) => {
          // Mécanisme M4 — auto-logout sur invalidation serveur de session.
          // Préconditions cumulées (toutes nécessaires) :
          //  1. err est un HttpErrorResponse 401
          //  2. on a réellement attaché un Bearer : sinon le 401 est *attendu*
          //     (cas d'une route authentifiée appelée sans session) et ne
          //     reflète pas une session-jadis-vivante-désormais-morte.
          //  3. pas déjà un logout en cours (anti-réentrance multi-requêtes).
          // 403 délibérément exclu : "authentifié mais pas autorisé" ≠ session morte.
          if (
            err instanceof HttpErrorResponse
            && err.status === 401
            && hadToken
            && !autoLogoutInFlight
          ) {
            autoLogoutInFlight = true;
            // Résolution juste-à-temps : à ce point HttpClient est entièrement construit,
            // pas de risque de cycle DI.
            const userAuthService = injector.get(UserAuthService);
            userAuthService.logout()
              .catch(() => { /* ne jamais bloquer la chaîne d'erreur d'origine */ })
              .finally(() => { autoLogoutInFlight = false; });
          }
          return throwError(() => err);
        })
      );
    })
  );
};

/**
 * Fonction pour obtenir le token de manière asynchrone
 */
async function getToken(
  req: HttpRequest<unknown>,
  keycloakService: KeycloakAuthService,
  sharedFacade: SharedFacade,
  router: Router
): Promise<string | null> {
  try {
    let token: string | null = null;

    // --- CORRECTION: Priorité au token OTP si on est sur un flux guest et que le token est valide ---
    const isGuestRoute = router.url.includes('/consultation') || 
                         router.url.includes('/guest-payment') || 
                         router.url.includes('/correct') ||
                         router.url.includes('/consulter') ||
                         router.url.includes('/demandes-consultation');
    
    if (isGuestRoute) {
      const isConsultApi = req.url.includes('/records/consult') ||
                           req.url.includes('/records/anonymous/consult') ||
                           // `/records/{recordId}/timeline` : le test littéral
                           // `/records/timeline` d'origine ne pouvait jamais
                           // correspondre — l'identifiant s'intercale toujours
                           // (cf. `requests.api.ts`, `getRecordTimeline`).
                           /\/records\/[^/]+\/timeline/.test(req.url) ||
                           req.url.includes('/records/otp') ||
                           req.url.includes('/no-account/') ||
                           req.url.includes('/portal-migration/') ||
                           req.url.includes('/file/') ||
                           req.url.includes('/documents/') ||
                           req.url.includes('/requests/');
      if (isConsultApi) {
        const otpToken = jetonInviteVivant(sharedFacade);
        if (otpToken) {
          return otpToken;
        }
      }
    }

    // 1. Essayer d'obtenir le token depuis KeycloakService
    try {
      const isLoggedIn = await keycloakService.isLoggedIn();
      if (isLoggedIn) {
        await keycloakService.updateToken(5);
        token = await keycloakService.getToken();
      }
    } catch (_error) {
    }

    // 2. Si pas de token, vérifier le stockage
    //    `keycloak_access_token` reste en localStorage : son partage entre
    //    onglets est la définition même du SSO et doit être préservé.
    //    Les deux jetons invités, eux, sont désormais propres à l'onglet.
    if (!token) {
      token = sharedFacade.getFromStorage('keycloak_access_token') as string ||
        jetonInviteVivant(sharedFacade) ||
        null;
    }

    return token;
  } catch (_error) {
    return null;
  }
}

/**
 * Jeton de consultation de **cet onglet**, ou `null` s'il est absent ou périmé.
 *
 * Point unique de lecture des deux clés invitées, pour les deux chemins de
 * `getToken` : le chemin rapide des routes invitées comme le repli générique.
 *
 * Le repli générique servait auparavant le jeton **sans jamais regarder son
 * horodatage** : une consultation expirée continuait donc d'émettre un Bearer
 * mort, ce qui provoquait un 401 — et, l'intercepteur croyant avoir attaché une
 * session vivante, déclenchait la déconnexion Keycloak forcée du bloc M4 pour
 * un usager invité qui n'a pourtant aucune session SSO. Un jeton périmé ne doit
 * plus jamais quitter le navigateur.
 *
 * Les deux clés vivent dans le stockage isolé par onglet, tout comme
 * l'horodatage — c'est la symétrie qui manquait et dont l'absence provoquait la
 * collision entre onglets.
 */
function jetonInviteVivant(sharedFacade: SharedFacade): string | null {
  const expiresAt = parseInt(sharedFacade.getFromSession('consult_token_expires_at') || '0', 10);
  if (!(expiresAt > Date.now())) {
    return null;
  }
  return sharedFacade.getFromSession('consult_access_token') ||
         sharedFacade.getFromSession('access_token') ||
         null;
}
